import _ from "lodash";
/** external */
import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Checkbox, Tooltip } from "@toddle-edu/ds-web";
import { connect } from "react-redux";
/** internal */
import { useI18n } from "Hooks";
import { getStyleStrippedText } from "Utils";
import { updateSelectedLibraryQuestions } from "Worksheet/modules/WorksheetModules";
import {
  QUESTION_TAG_TOPIC,
  QUESTION_IMPORT_LIMIT,
  customTextStyle,
} from "Worksheet/modules/WorksheetConstants";
import { getDifficultyLevelOptions } from "QuestionBank/modules/Utils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import WorksheetTextView from "UIComponents/WorksheetTextView";

/** relative */
import classes from "./QuestionCard.scss";

const QuestionCard = props => {
  const {
    selectedQuestions,
    isPreviewed,
    onClick,
    isFromSelectionFeed,
    libraryCardDetails,
    addQuestionToSelection,
    disableSelection,
  } = props;
  const {
    id,
    questionTypeLabel,
    marks,
    title,
    tagLabels,
    difficulty,
  } = libraryCardDetails;

  const { t } = useI18n(["common", "worksheet"]);

  const isSelected = _.some(selectedQuestions, ["id", id]);
  const isCheckBoxDisabled = disableSelection && !isSelected;

  const onAddClick = e => {
    if (!isCheckBoxDisabled) {
      e?.stopPropagation?.();
      addQuestionToSelection({ isSelected, id, libraryCardDetails });
    }
  };

  const _onClick = () => {
    onClick && onClick(id);
  };

  const headerLeftClasses = classNames(classes.headerLeft, "text-subtitle-3");
  const marksClasses = classNames(classes.marks, "text-subtitle-3");
  const titleLabelClasses = classNames(classes.titleLabel, "heading-6 w-full");
  const containerClasses = classNames(classes.container, {
    [classes.previewContainer]: isPreviewed,
    [classes.widthUnset]: isFromSelectionFeed,
  });
  const tagsClasses = classNames(classes.tags, "text-body-2");

  const difficultyLevelOptions = getDifficultyLevelOptions(t);
  const difficultyLevel = _.find(difficultyLevelOptions, {
    value: difficulty,
  });

  const sanitizedTitle = getStyleStrippedText({
    text: title,
    type: "DOCUMENT_EDITOR",
  });

  return (
    <div
      key={id}
      className={containerClasses}
      onClick={_onClick}
      tabIndex={0}
      onKeyDown={event => {
        if (event.key === "Enter") {
          _onClick();
        }
      }}
      data-testid={`question-library-question-card-container`}
    >
      {/* header */}
      <div className={classes.header}>
        <div className={headerLeftClasses}>
          <div className={classes.questionTypeLabel}>{questionTypeLabel}</div>
        </div>
        <div className={classes.headerRight}>
          <div className={marksClasses}>
            {t("common:mark_with_number", {
              count: marks,
            })}
          </div>

          <ConditionalWrapper
            type={"wrapperAsElement"}
            condition={isCheckBoxDisabled}
            wrapper={
              <Tooltip
                tooltip={t(
                  "worksheet:you_can_only_add_up_to_questions_at_a_time_with_count",
                  { count: QUESTION_IMPORT_LIMIT }
                )}
                placement="bottom"
                overlayInnerStyle={{
                  maxWidth: "136px",
                  textAlign: "center",
                }}
              />
            }
          >
            <Checkbox
              dsVersion="2.0"
              isChecked={isSelected}
              onClick={onAddClick}
              disabled={isCheckBoxDisabled}
              testId={`question-library-question-card-checkbox`}
            />
          </ConditionalWrapper>
        </div>
      </div>
      {/* title */}
      <div className={classes.title}>
        <div className={titleLabelClasses}>
          <WorksheetTextView
            value={sanitizedTitle}
            mode={"view"}
            customTextStyle={customTextStyle}
            isRtlAlignmentRequired={true}
          />
        </div>
      </div>
      {/* tags */}
      <div className={tagsClasses}>
        <div className={classes.tagContainer}>
          {_.get(tagLabels, QUESTION_TAG_TOPIC)}

          {_.get(difficultyLevel, "label") ? (
            <>
              {_.get(tagLabels, QUESTION_TAG_TOPIC) ? (
                <div className={classes.dotSeparator}>{" • "}</div>
              ) : null}
              <div>{_.get(difficultyLevel, "label")}</div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => {
  const selectedQuestions = _.get(
    state,
    "worksheet.questionLibrary.selectedQuestions",
    []
  );

  return {
    selectedQuestions,
  };
};

const mapActionCreators = {
  updateSelectedLibraryQuestions,
};

QuestionCard.propTypes = {
  id: PropTypes.string,
  questionTypeLabel: PropTypes.string,
  marks: PropTypes.number,
  title: PropTypes.string,
  isPreviewed: PropTypes.bool,
  onClick: PropTypes.func,
  tagLabels: PropTypes.object,
  difficulty: PropTypes.string,
};
QuestionCard.defaultProps = {
  isPreviewed: false,
  tagLabels: {},
  onClick: _.noop,
};

export default connect(mapStateToProps, mapActionCreators)(QuestionCard);
